'use client';

import type { PlatoEventMap, PlatoInitOptions } from '@plato/embed';
import { type CSSProperties, createElement, useEffect, useRef } from 'react';
import { loadPlato, plato } from '../load-plato.ts';

export type PlatoEventHandlers = {
  readonly [E in keyof PlatoEventMap]?: (detail: PlatoEventMap[E]) => void;
};

export interface PlatoAssistantProps extends Omit<PlatoInitOptions, 'container'> {
  /** Handlers for the loader's events, by name. They may change between renders. */
  readonly on?: PlatoEventHandlers;
  /** On the `<plato-assistant>` element, for `mode="panel"` and `mode="inline"`. */
  readonly className?: string;
  readonly style?: CSSProperties;
}

/**
 * The assistant on the page of a signed-in user. With `mode="panel"` or `mode="inline"` it
 * renders the `<plato-assistant>` element where it stands; otherwise it renders nothing and the
 * side panel is fixed to the window. It starts the loader when mounted and removes the assistant
 * when unmounted; `context` and `appearance` follow the props.
 */
export function PlatoAssistant(props: PlatoAssistantProps) {
  const latest = useRef(props);
  latest.current = props;
  const sent = useRef({ context: '', appearance: '' });
  const startKey = JSON.stringify(startShape(props));
  const contextKey = JSON.stringify(props.context ?? null);
  const appearanceKey = JSON.stringify(props.appearance ?? null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: everything init reads is in startKey
  useEffect(() => {
    const current = latest.current;
    const unsubscribe = subscribe(() => latest.current.on);
    loadPlato(current.embedOrigin !== undefined ? { embedOrigin: current.embedOrigin } : {}).catch(
      (error: unknown) =>
        latest.current.on?.error?.({
          code: 'loader_error',
          message: error instanceof Error ? error.message : String(error),
        }),
    );
    plato(
      'init',
      initOptions(current, () => latest.current.getClientSecret),
    );
    sent.current = { context: contextKey, appearance: appearanceKey };
    return () => {
      unsubscribe();
      plato('destroy');
    };
  }, [startKey]);

  useEffect(() => {
    const { context } = latest.current;
    if (sent.current.context === contextKey || context === undefined) return;
    sent.current.context = contextKey;
    plato('setContext', context);
  }, [contextKey]);

  useEffect(() => {
    const { appearance } = latest.current;
    if (sent.current.appearance === appearanceKey || appearance === undefined) return;
    sent.current.appearance = appearanceKey;
    plato('setAppearance', appearance);
  }, [appearanceKey]);

  if (props.mode !== 'panel' && props.mode !== 'inline') return null;
  return createElement('plato-assistant', {
    mode: props.mode,
    className: props.className,
    style: props.style,
  });
}

/** What `init` reads once: a change to any of it starts the assistant again. */
function startShape(props: PlatoAssistantProps): Record<string, unknown> {
  const {
    on: _on,
    className: _className,
    style: _style,
    context: _context,
    appearance: _appearance,
    getClientSecret,
    ...rest
  } = props;
  return { ...rest, hasGetClientSecret: getClientSecret !== undefined };
}

function initOptions(
  props: PlatoAssistantProps,
  getter: () => PlatoInitOptions['getClientSecret'],
): PlatoInitOptions {
  const { on: _on, className: _className, style: _style, getClientSecret, ...options } = props;
  if (getClientSecret === undefined) return options;
  return { ...options, getClientSecret: () => getter()?.() ?? Promise.resolve(null) };
}

function subscribe(handlers: () => PlatoEventHandlers | undefined): () => void {
  const listeners = EVENT_NAMES.map((name) => {
    const listener = (detail: PlatoEventMap[keyof PlatoEventMap]) => {
      const handler = handlers()?.[name] as ((value: typeof detail) => void) | undefined;
      handler?.(detail);
    };
    plato('on', name, listener);
    return { name, listener };
  });
  return () => {
    for (const { name, listener } of listeners) plato('off', name, listener);
  };
}

const EVENTS: Record<keyof PlatoEventMap, true> = {
  ready: true,
  open: true,
  close: true,
  expand: true,
  collapse: true,
  unread: true,
  error: true,
  signedOut: true,
  'message.sent': true,
  'response.completed': true,
  'response.failed': true,
  'file.downloaded': true,
  'conversation.started': true,
  'session.ended': true,
};
const EVENT_NAMES = Object.keys(EVENTS) as Array<keyof PlatoEventMap>;
